/**
 * 앱 전역 시각 표시 규약.
 *
 * wire 파싱은 `public.ts` 의 `parseUtcMs` 가 소유하고 이 모듈은 그 결과(ms 정수)를 문자열로
 * 옮긴다. 호출부가 `formatDateTime(parseUtcMs(iso))` 로 쓰는 것은 장황해 보여도 "이 값이
 * 파서를 탔나" 를 한 줄로 답하게 만드는 것이 요점이다. `Date` 객체는 이 모듈 밖으로 나가지 않는다.
 *
 * 절대 시각을 로케일에 맡기지 않는다 — 로케일 출력은 폭이 들쭉날쭉해 표 컬럼 폭과 충돌하고,
 * ICU 버전이 오르면 문자열이 바뀌어 단언이 깨진다(규칙 §7).
 *
 * Owns:
 * - 절대 시각 · 길이의 표시 문자열과 값 없음의 표기(`—`).
 *
 * Does not own:
 * - wire 문자열 해석 — `public.ts`. 도메인별 상태 계산 — 각 모듈의 feature.
 */
import { parseUtcMs } from "./public";

export { parseUtcMs };

const EMPTY = "—";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function isRenderable(ms: number | null): ms is number {
  return ms !== null && Number.isFinite(ms);
}

/** 목록 · 상세의 기본 시각 표시. 예) `2026-09-09 21:34` (뷰어 타임존). */
export function formatDateTime(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }

  const date = new Date(ms);
  const day = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  return `${day} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * 대략적인 길이. 예) `12s` / `3m 5s` / `1h 2m`.
 *
 * 시 단위에서 초를 버리는 것은 그 자리에서 초가 의미를 갖지 않기 때문이고, 분 단위에서는
 * 남긴다 — 짧은 작업은 초 차이가 곧 정보다.
 */
export function formatDuration(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }

  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${totalSeconds % 60}s`;
  }

  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
